import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import {
  ArrowLeft, ArrowRight, Download, Printer, LogIn, TriangleAlert, X, QrCode as QrCodeIcon,
  Palette, Sparkles, Grid3x3, FolderTree, Link2, MousePointerClick, Lock, Image as ImageIcon,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { setSeoMetadata } from '../lib/seo';
import { buildQrData, ERROR_CORRECTION_LEVELS, savePendingQrDraft } from '../lib/qrEncode';

/**
 * Free Tools -> QR Code Generator.
 *
 * An info/marketing landing page (what it does, why it's free, what's
 * unlocked by registering) with a CTA that slides open the actual generator
 * in a right-hand drawer - same slide-out pattern as the mobile nav.
 *
 * The generator itself is deliberately simple: a link QR code, basic
 * styling, nothing saved. Every other content type, custom dot/corner
 * shapes, gradients, a logo, saving, projects, and dynamic/editable links
 * are premium tools reserved for a registered, signed-in account (staff or
 * self-registered - QR Builder is full-use for anyone with an account, not
 * just staff). "Sign in to save it" carries whatever link was typed here
 * across to QR Builder once they're in, so registering doesn't mean
 * starting over.
 */
const inputClass =
  'w-full px-4 py-2.5 bg-[#1a1c1b] border border-[#4a3520] rounded-lg text-white placeholder-[#666] ' +
  'focus:outline-none focus:border-[#FF6B00] transition-colors';

const FEATURES = [
  { icon: Grid3x3, title: '13 QR code types', body: 'URL, text, phone, SMS, email, WhatsApp, WiFi, vCard, crypto, PayPal and more.' },
  { icon: Lock, title: 'Privacy first', body: "We don't store anything about a QR code you create here unless you choose to save it." },
  { icon: Palette, title: 'Highly customizable', body: 'Brand it with your own colors, shapes, gradients, and logo.' },
  { icon: FolderTree, title: 'Projects', body: 'Organize your saved QR codes into color-coded projects.' },
];

const QR_KINDS = [
  {
    icon: ImageIcon,
    title: 'Static QR codes',
    description: 'All the data is embedded directly into the code. Once generated, it can’t be changed — print it and it works forever, exactly as created.',
    points: ['Fully customizable', 'No scan limit', 'Nothing saved on our servers unless you save it'],
  },
  {
    icon: Link2,
    title: 'Dynamic QR codes',
    description: 'The code encodes a permanent short link instead of the content itself, so you can change where it points anytime — without reprinting anything.',
    points: ['Change the destination anytime', 'Scan count tracking', 'Requires a free account to create'],
  },
];

const PREMIUM_PERKS = [
  'Every QR type: WiFi, vCard, email, SMS, WhatsApp, crypto, PayPal, events and more',
  'Custom dot & corner shapes, gradients, and your own logo',
  'Dynamic QR codes you can re-point anytime without reprinting',
  'Saved QR codes organized into color-coded projects',
];

const QrCodeGeneratorPage = () => {
  const { siteName } = useSiteSettings();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const printRef = useRef(null);
  const qrInstanceRef = useRef(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [ecc, setEcc] = useState('M');

  useEffect(() => {
    setSeoMetadata({
      title: 'Free QR Code Generator',
      description:
        'Turn any link into a QR code - free, no account. Create it, download or print it. '
        + 'Register for free to unlock every QR type, custom styling, logos, and dynamic codes.',
      keywords: 'free qr code generator, link qr code, make a qr code, qr code maker',
      canonicalPath: '/tools/qr-code',
    });
  }, [siteName]);

  // Lock background scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const data = buildQrData('url', { url }) || ' ';

  useEffect(() => {
    if (!previewRef.current) return;
    const timeoutId = setTimeout(() => {
      const options = {
        data,
        margin: 10,
        qrOptions: { errorCorrectionLevel: ecc },
        dotsOptions: { type: 'square', color: fgColor },
        cornersSquareOptions: { type: 'square', color: fgColor },
        cornersDotOptions: { type: 'square', color: fgColor },
        backgroundOptions: { color: bgColor },
      };
      if (!qrInstanceRef.current) {
        qrInstanceRef.current = new QRCodeStyling({ width: 240, height: 240, ...options });
        qrInstanceRef.current.append(previewRef.current);
      } else {
        qrInstanceRef.current.update(options);
      }
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [data, fgColor, bgColor, ecc]);

  const handleDownload = (extension) => {
    if (!qrInstanceRef.current) return;
    qrInstanceRef.current.download({ name: 'qr-code', extension });
  };

  const handlePrint = () => {
    const canvas = previewRef.current?.querySelector('canvas');
    if (!canvas || !printRef.current) return;
    printRef.current.innerHTML = '';
    const img = document.createElement('img');
    img.src = canvas.toDataURL('image/png');
    img.style.width = '320px';
    img.style.height = '320px';
    printRef.current.appendChild(img);
    window.print();
  };

  const handleSignInToSave = () => {
    savePendingQrDraft({ type: 'url', content: { url }, settings: { fgColor, bgColor, ecc } });
    navigate('/login');
  };

  return (
    <>
      <div className="min-h-screen bg-[#1a1c1b] print:hidden">
        <Header />

        {/* Hero / info landing page */}
        <section className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Link
              to="/tools"
              className="inline-flex items-center gap-1.5 text-[#888680] hover:text-[#FF6B00] text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              All free tools
            </Link>
            <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
              FREE TOOLS
            </span>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF6B00]/10 mb-6">
              <QrCodeIcon className="w-8 h-8 text-[#FF6B00]" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">QR Code Generator</h1>
            <p className="text-[#888680] text-lg max-w-xl mx-auto mb-8">
              Turn any link into a QR code in seconds &mdash; free, no account needed. Download it, print
              it, done.
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full text-lg transition-colors"
              data-testid="open-qr-generator-button"
            >
              Create Your QR Code <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Feature highlights */}
        <section className="pb-16">
          <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-6 text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FF6B00]/10 text-[#FF6B00] mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="text-white font-semibold mb-1.5">{f.title}</h3>
                <p className="text-[#888680] text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Static vs Dynamic explainer */}
        <section className="pb-16">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-white font-bold text-2xl text-center mb-2">Static or dynamic?</h2>
            <p className="text-[#888680] text-center mb-10 max-w-xl mx-auto">
              Every QR code you make here is static. Register for free to make it dynamic instead.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {QR_KINDS.map((kind) => (
                <div key={kind.title} className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-6">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FF6B00]/10 text-[#FF6B00] mb-4">
                    <kind.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{kind.title}</h3>
                  <p className="text-[#888680] text-sm mb-4">{kind.description}</p>
                  <ul className="space-y-1.5">
                    {kind.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-[#dfddd6] text-sm">
                        <MousePointerClick className="w-3.5 h-3.5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Premium upsell */}
        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-4">
            <div className="bg-gradient-to-br from-[#FF6B00]/10 to-transparent border border-[#FF6B00]/30 rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#FF6B00]" />
                <h2 className="text-white font-bold text-xl">Want more? Register for free.</h2>
              </div>
              <ul className="space-y-2 mb-6">
                {PREMIUM_PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-[#dfddd6] text-sm">
                    <Palette className="w-4 h-4 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                    {perk}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="inline-flex items-center gap-1.5 px-6 py-3 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full transition-colors"
              >
                Create Your Free Account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Drawer backdrop */}
      <div
        className={`print:hidden fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* Generator - slides in from the right */}
      <div
        className={`print:hidden fixed inset-y-0 right-0 z-50 w-[90vw] max-w-md bg-[#1a1c1b] border-l border-[#4a3520] shadow-2xl overflow-y-auto transition-transform duration-300 ease-in-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="qr-generator-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#4a3520] sticky top-0 bg-[#1a1c1b] z-10">
          <h2 className="text-white font-semibold">Create Your QR Code</h2>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 text-[#888680] hover:text-white" data-testid="close-qr-generator-button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="space-y-2">
            <label className="text-sm text-[#dfddd6]">URL</label>
            <input
              className={inputClass}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              data-testid="qr-url-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-[#dfddd6]">Foreground</label>
              <div className="flex items-center gap-2">
                <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} className="w-10 h-10 rounded border border-[#4a3520] bg-transparent flex-shrink-0" />
                <input className={inputClass} value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#dfddd6]">Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-10 h-10 rounded border border-[#4a3520] bg-transparent flex-shrink-0" />
                <input className={inputClass} value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-[#dfddd6]">Error Correction</label>
            <select className={inputClass} value={ecc} onChange={(e) => setEcc(e.target.value)}>
              {ERROR_CORRECTION_LEVELS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-5 flex flex-col items-center gap-4">
            <div ref={previewRef} className="rounded-lg p-2 bg-white" data-testid="qr-preview" />
            <div className="grid grid-cols-3 gap-2 w-full">
              <button
                onClick={() => handleDownload('png')}
                className="flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg border border-[#4a3520] text-[#dfddd6] hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" /> PNG
              </button>
              <button
                onClick={() => handleDownload('svg')}
                className="flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg border border-[#4a3520] text-[#dfddd6] hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" /> SVG
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg border border-[#4a3520] text-[#dfddd6] hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors text-sm font-medium"
                data-testid="qr-print-button"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <TriangleAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold mb-1 text-sm">This QR code isn't saved</h3>
                <p className="text-[#888680] text-xs mb-3">
                  Download or print it now &mdash; close this and it's gone for good. Register for free to
                  unlock every QR type, custom styling, logos, and dynamic codes.
                </p>
                <button
                  type="button"
                  onClick={handleSignInToSave}
                  className="inline-flex items-center gap-1.5 text-[#FF6B00] hover:underline text-sm font-semibold"
                >
                  <LogIn className="w-4 h-4" /> Sign in to save it
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only: just the QR, nothing else on the page */}
      <div ref={printRef} className="hidden print:flex print:fixed print:inset-0 print:items-center print:justify-center print:bg-white" />
    </>
  );
};

export default QrCodeGeneratorPage;
